import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Copy, Check, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BuyerTransferDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [transfer, setTransfer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    fetchTransferDetail();
  }, [id]);

  const fetchTransferDetail = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/buyer/transfers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransfer(res.data.transfer);
      setError(null);
    } catch (err) {
      console.error('Error fetching transfer:', err);
      if (err.response?.status === 404) {
        setError('Transfer not found');
      } else if (err.response?.status === 403) {
        setError('Access denied');
      } else {
        setError('Failed to load transfer details');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(label);
    toast.success(`${label} copied!`);
    setTimeout(() => setCopied(null), 2000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#0B0B0B' }}>
        Loading transfer details...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px' }}>
        <button
          onClick={() => navigate('/buyer/transfers')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'transparent',
            border: 'none',
            color: '#4B9DFF',
            cursor: 'pointer',
            marginBottom: '24px',
            fontSize: '14px'
          }}
        >
          <ArrowLeft size={18} /> Back to Transfers
        </button>
        <div style={{
          background: 'rgba(255,107,107,0.1)',
          border: '1px solid rgba(255,107,107,0.3)',
          borderRadius: '12px',
          padding: '24px',
          color: '#FF6B6B'
        }}>
          {error}
        </div>
      </div>
    );
  }

  if (!transfer) {
    return null;
  }

  return (
    <div style={{ padding: '40px' }}>
      <button
        onClick={() => navigate('/buyer/transfers')}
        data-testid="back-to-transfers"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: 'transparent',
          border: 'none',
          color: '#4B9DFF',
          cursor: 'pointer',
          marginBottom: '24px',
          fontSize: '14px'
        }}
      >
        <ArrowLeft size={18} /> Back to Transfers
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
        {/* Left: Image */}
        <div style={{
          background: '#FFFFFF',
          borderRadius: '16px',
          overflow: 'hidden',
          border: '1px solid #E6E8EF'
        }}>
          <div style={{
            width: '100%',
            height: '300px',
            background: transfer.image_url
              ? `url(${transfer.image_url}) center/cover`
              : 'linear-gradient(135deg, #7C3AED, #4B9DFF)'
          }} />
        </div>

        {/* Right: Details */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <span style={{
              fontSize: '11px',
              padding: '4px 8px',
              borderRadius: '4px',
              fontWeight: 600,
              background: 'rgba(71, 255, 176, 0.1)',
              color: '#47FFB0'
            }}>
              {transfer.type}
            </span>
            <span style={{
              fontSize: '11px',
              padding: '4px 8px',
              borderRadius: '4px',
              fontWeight: 600,
              background: transfer.status === 'COMPLETED' ? 'rgba(71, 255, 176, 0.1)' : 'rgba(255, 193, 7, 0.1)',
              color: transfer.status === 'COMPLETED' ? '#47FFB0' : '#FFC107'
            }}>
              {transfer.status}
            </span>
          </div>

          <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#0B0B0B', marginBottom: '8px' }}>
            {transfer.portfolio_name}
          </h1>
          <p style={{ fontSize: '16px', color: '#999', marginBottom: '24px' }}>
            {transfer.serial ? `Token ${transfer.serial}` : `Token #${transfer.token_id || 'N/A'}`}
          </p>

          {/* Details Card */}
          <div style={{
            background: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            border: '1px solid #E6E8EF'
          }}>
            <h3 style={{ fontSize: '14px', color: '#999', marginBottom: '20px' }}>Transfer Details</h3>

            <div style={{ display: 'grid', gap: '16px' }}>
              <DetailRow label="Transfer ID" value={transfer.id} canCopy onCopy={() => copyToClipboard(transfer.id, 'ID')} copied={copied === 'ID'} />
              <DetailRow label="Portfolio ID" value={transfer.portfolio_id} canCopy onCopy={() => copyToClipboard(transfer.portfolio_id, 'Portfolio')} copied={copied === 'Portfolio'} />
              {transfer.nft_id && <DetailRow label="NFT ID" value={transfer.nft_id} canCopy onCopy={() => copyToClipboard(transfer.nft_id, 'NFT')} copied={copied === 'NFT'} />}
              <DetailRow label="From" value={transfer.from} />
              <DetailRow label="To" value={transfer.to} />
              <DetailRow label="Date" value={formatDate(transfer.occurred_at)} />
              {transfer.gross_amount !== undefined && (
                <DetailRow label="Amount" value={`$${transfer.gross_amount}`} highlight />
              )}
              {transfer.payment_method && (
                <DetailRow label="Payment Method" value={transfer.payment_method} />
              )}
              {transfer.tx_hash && (
                <DetailRow 
                  label="Transaction" 
                  value={transfer.tx_hash.substring(0, 20) + '...'} 
                  canCopy 
                  onCopy={() => copyToClipboard(transfer.tx_hash, 'TX')} 
                  copied={copied === 'TX'}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DetailRow = ({ label, value, canCopy, onCopy, copied, highlight }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <span style={{ fontSize: '13px', color: '#666' }}>{label}</span>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ 
        fontSize: '14px', 
        color: highlight ? '#6C5CE7' : '#0B0B0B',
        fontWeight: highlight ? 600 : 400,
        fontFamily: canCopy ? 'monospace' : 'inherit'
      }}>
        {value || 'N/A'}
      </span>
      {canCopy && value && (
        <button
          onClick={onCopy}
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px' }}
        >
          {copied ? <Check size={14} color="#47FFB0" /> : <Copy size={14} color="#4B9DFF" />}
        </button>
      )}
    </div>
  </div>
);

export default BuyerTransferDetail;
